<template>
  <div class="operator-search-container">
    <div class="input-with-clear">
      <b-form-input :id="id" v-model="searchQuery" @input="onSearch" @focus="onFocus" @blur="onBlur" autocomplete="off"
        :placeholder="placeholder" required :state="state" :disabled="isLoading" />
      <div v-if="isLoading" class="spinner-container">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <button v-if="searchQuery" type="button" class="clear-button" @click="clearValue">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div v-if="showResults" class="operator-search-results">
      <div v-for="operator in filteredOperators" :key="operator.value" class="operator-search-item"
        @click="onSelect(operator)">
        {{ operator.text }}
      </div>
    </div>
    <b-form-invalid-feedback :state="state" v-if="touched">
      {{ errorMessage }}
    </b-form-invalid-feedback>
  </div>
</template>

<script>
export default {
  name: 'SingleFieldOperator',

  props: {
    value: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: 'Operator'
    },
    placeholder: {
      type: String,
      default: 'Enter Operator'
    },
    state: {
      type: [Boolean, null],
      default: null
    },
    touched: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: 'This field is required'
    }
  },

  data() {
    return {
      searchQuery: '',
      showResults: false,
      operators: [],
      lastSelectedOperator: null,
      isLoading: true,
      operatorsFromStore: []
    }
  },

  async created() {
    await this.$store.dispatch('operatorsForSelect/loadList');
    this.operatorsFromStore = this.$store.getters['operatorsForSelect/list'];
    await this.loadOperators();
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.searchQuery = '';
          this.lastSelectedOperator = null;
        } else if (newValue !== this.searchQuery) {
          const operator = this.operators.find(op => op.value === newValue);
          this.searchQuery = operator ? operator.text : newValue;
        }
      }
    }
  },

  computed: {
    filteredOperators() {
      const search = this.searchQuery.toLowerCase();
      if (!search && this.showResults) {
        return this.operators;
      }

      return this.operators.filter(op => {
        const text = op.text.toLowerCase();
        return text.includes(search);
      });
    }
  },

  methods: {
    async loadOperators() {
      try {
        this.isLoading = true;

        if (!this.operatorsFromStore) {
          console.error('No operators received from store');
          return;
        }

        this.operators = this.operatorsFromStore.map(option => ({
          value: option.value,
          text: option.label
        })) || [];

        if (this.value) {
          this.lastSelectedOperator = this.operators.find(op => op.value === this.value);
        }
      } catch (error) {
        console.error('Error loading operators:', error);
      } finally {
        this.isLoading = false;
      }
    },

    onSearch() {
      this.showResults = true;
      if (this.searchQuery !== (this.lastSelectedOperator?.text || '')) {
        this.$emit('input', '');
      }
    },

    onFocus() {
      this.showResults = true;
    },

    onSelect(operator) {
      this.$emit('input', operator.value);
      this.searchQuery = operator.text;
      this.showResults = false;
      this.lastSelectedOperator = operator;
      this.$emit('selected', operator);
    },

    handleClickOutside(event) {
      if (!event.target.closest('.operator-search-container')) {
        this.showResults = false;
        if (this.lastSelectedOperator && this.searchQuery !== this.lastSelectedOperator.text) {
          this.searchQuery = this.lastSelectedOperator.text;
          this.$emit('input', this.lastSelectedOperator.value);
          this.$emit('blur');
        }
      }
    },

    clearValue() {
      this.searchQuery = '';
      this.$emit('input', '');
      this.lastSelectedOperator = null;
      this.showResults = false;
      this.$emit('blur');
    },

    onBlur(event) {
      this.$emit('blur', event);
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
}
</script>

<style scoped>
.operator-search-container {
  position: relative;
}

.operator-search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ced4da;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.operator-search-item {
  padding: 8px 12px;
  cursor: pointer;
}

.operator-search-item:hover {
  background-color: #f8f9fa;
}

.input-with-clear {
  position: relative;
  display: flex;
  align-items: center;
}

.clear-button {
  position: absolute;
  right: 25px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 12px;
}

.clear-button:hover {
  color: #343a40;
}

.clear-button:focus {
  outline: none;
}

.input-with-clear .form-control {
  padding-right: 50px;
}

.invalid-feedback {
  position: absolute;
  margin-top: 2px;
}

.form-group {
  margin-bottom: 25px;
}

.spinner-container {
  position: absolute;
  right: 50px;
  top: 50%;
  transform: translateY(-50%);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>